/**
 * BAIS Lazy Load Controller
 * 
 * Handles lazy loading for before/after image sliders
 */

class BAISLazyLoader {
    constructor() {
        this.sliders = new Map();
        this.loadedSliders = new Set();
        this.loadedImageUrls = new Set(); // Prevent duplicate image loading
        this.settings = window.cocaBAISLazyLoad || {};
        this.observer = null;
        
        this.init();
    }

    /**
     * Initialize the lazy loader
     */
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
            this.setup();
        }
    }

    /**
     * Setup lazy loading
     */
    setup() {
        this.findSliders();
        this.createObserver();
        this.observeSliders();
        this.handleMultipleSliders();
    }

    /**
     * Find all BAIS sliders on the page
     */
    findSliders() {
        // Find both regular and triple sliders
        const sliderContainers = document.querySelectorAll('.coca-bais-container, .coca-bais-triple-container');
        
        sliderContainers.forEach((container, index) => {
            const lazyImages = container.querySelectorAll('.coca-bais-lazy');
            
            if (lazyImages.length > 0) {
                // Create unique slider ID to handle duplicate IDs
                const originalId = container.id;
                const uniqueId = originalId ? `${originalId}-lazy-${index}` : `bais-slider-${index}`;
                
                // Store unique ID on the container for later reference
                container.setAttribute('data-bais-lazy-id', uniqueId);
                
                const beforeImage = container.querySelector('.coca-bais-lazy-before');
                const middleImage = container.querySelector('.coca-bais-lazy-middle');
                const afterImage = container.querySelector('.coca-bais-lazy-after');
                
                // Check if images or videos actually need lazy loading (have data-src)
                const needsLoading = Array.from(lazyImages).some(img => img.dataset.src) ||
                                   Array.from(container.querySelectorAll('.coca-bais-lazy-source')).some(source => source.dataset.src);
                
                this.sliders.set(uniqueId, {
                    container: container,
                    images: lazyImages,
                    beforeImage: beforeImage,
                    middleImage: middleImage,
                    afterImage: afterImage,
                    isVisible: false,
                    isLoaded: !needsLoading, // Mark as loaded if no data-src found
                    priority: index // Lower index = higher priority
                });
                
                // If no lazy loading needed, mark images as loaded immediately
                if (!needsLoading) {
                    this.markImagesAsLoaded(lazyImages);
                }
            }
        });
    }

    /**
     * Create intersection observer
     */
    createObserver() {
        const options = {
            rootMargin: this.settings.threshold || '50px',
            threshold: 0.1
        };

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                // Use the unique lazy ID we stored on the container
                const container = entry.target;
                const sliderId = container.getAttribute('data-bais-lazy-id');
                
                if (sliderId && this.sliders.has(sliderId)) {
                    const slider = this.sliders.get(sliderId);
                    slider.isVisible = entry.isIntersecting;
                    
                    if (entry.isIntersecting && !slider.isLoaded && 
                        !slider.container.classList.contains('coca-bais-loading') &&
                        !slider.container.classList.contains('coca-bais-loaded')) {
                        // Add a delay to show skeleton effect
                        setTimeout(() => {
                            // Double check slider is still eligible for loading
                            if (!slider.isLoaded && !this.loadedSliders.has(sliderId)) {
                                this.loadSlider(sliderId);
                            }
                        }, 300); // 300ms delay
                    }
                }
            });
        }, options);
    }

    /**
     * Start observing sliders
     */
    observeSliders() {
        const strategy = this.settings.strategy || 'progressive';
        
        this.sliders.forEach((slider, sliderId) => {
            // For immediate strategy, only observe sliders beyond the first 3
            if (strategy === 'immediate') {
                const sliderArray = Array.from(this.sliders.entries());
                const sliderIndex = sliderArray.findIndex(([id]) => id === sliderId);
                
                // Only observe sliders starting from index 3 (4th slider and beyond)
                if (sliderIndex >= 3) {
                    this.observer.observe(slider.container);
                }
            } else {
                // For other strategies, observe all sliders except the first one (which loads immediately)
                const sliderArray = Array.from(this.sliders.entries());
                const sliderIndex = sliderArray.findIndex(([id]) => id === sliderId);
                
                if (sliderIndex > 0) {  // Skip first slider (index 0)
                    this.observer.observe(slider.container);
                }
            }
        });
    }

    /**
     * Handle multiple sliders strategy
     */
    handleMultipleSliders() {
        const strategy = this.settings.strategy || 'progressive';
        const sliderArray = Array.from(this.sliders.entries());
        
        if (strategy === 'immediate') {
            // Load first 3 sliders immediately, regardless of total count
            sliderArray.slice(0, 3).forEach(([sliderId]) => {
                this.loadSlider(sliderId);
            });
        } else if (strategy === 'progressive') {
            // Load first slider immediately, others on demand
            if (sliderArray.length > 0) {
                const firstSliderId = sliderArray[0][0];
                this.loadSlider(firstSliderId);
            }
        }
    }

    /**
     * Load a specific slider
     */
    async loadSlider(sliderId) {
        const slider = this.sliders.get(sliderId);
        
        if (!slider || slider.isLoaded || this.loadedSliders.has(sliderId)) {
            return;
        }

        // Double check to prevent race conditions
        if (slider.container.classList.contains('coca-bais-loading') || 
            slider.container.classList.contains('coca-bais-loaded')) {
            return;
        }

        this.loadedSliders.add(sliderId);
        slider.isLoaded = true;
        
        // Mark container as loading to prevent re-triggering
        slider.container.classList.add('coca-bais-loading');
        
        // Add skeleton class for triple sliders (fallback for browsers without :has() support)
        if (slider.container.classList.contains('coca-bais-triple-container')) {
            slider.container.classList.add('coca-bais-loading-skeleton');
        }

        try {
            // Show loading state
            this.showLoadingState(slider);

            // Ensure skeleton is visible for at least 1 second
            const startTime = Date.now();
            const minSkeletonTime = 1000; // 1 second minimum

            // Load images based on user intent
            const loadingStrategy = this.determineLoadingStrategy(slider);
            
            if (loadingStrategy === 'simultaneous') {
                await this.loadImagesPairs(slider);
            } else {
                await this.loadImagesProgressive(slider);
            }

            // Calculate remaining time for skeleton
            const elapsed = Date.now() - startTime;
            const remainingTime = Math.max(0, minSkeletonTime - elapsed);
            
            // Wait remaining time if needed
            if (remainingTime > 0) {
                await new Promise(resolve => setTimeout(resolve, remainingTime));
            }

            // Initialize slider after loading
            this.initializeSlider(slider);
            
            // Hide loading state
            this.hideLoadingState(slider);
            
            // Mark as fully loaded to prevent any re-triggering
            slider.container.classList.remove('coca-bais-loading');
            slider.container.classList.remove('coca-bais-loading-skeleton');
            slider.container.classList.add('coca-bais-loaded');
            
            // Stop observing this slider since it's loaded
            if (this.observer) {
                this.observer.unobserve(slider.container);
            }
            
            // Trigger custom event
            this.triggerLoadedEvent(slider);

        } catch (error) {
            console.error('BAIS Lazy Load Error:', error);
            
            // Remove from loaded set so it can be retried
            this.loadedSliders.delete(sliderId);
            slider.isLoaded = false;
            slider.container.classList.remove('coca-bais-loading');
            slider.container.classList.remove('coca-bais-loading-skeleton');
            
            this.handleLoadError(slider, error);
        }
    }

    /**
     * Determine loading strategy based on user intent
     */
    determineLoadingStrategy(slider) {
        // Check if user has shown intent to interact
        const hasHovered = slider.container.matches(':hover');
        const isNearViewport = this.isNearViewport(slider.container);
        const isMobile = window.innerWidth < 768;
        
        // Mobile users or users showing intent get simultaneous loading
        if (isMobile || hasHovered || isNearViewport) {
            return 'simultaneous';
        }
        
        return 'progressive';
    }

    /**
     * Check if element is near viewport
     */
    isNearViewport(element) {
        const rect = element.getBoundingClientRect();
        const threshold = 200; // pixels
        
        return rect.top < window.innerHeight + threshold &&
               rect.bottom > -threshold;
    }

    /**
     * Load media element (image, iframe, or video)
     */
    loadMedia(element) {
        if (!element) return Promise.resolve();

        // Prevent duplicate loading if src is already set to data-src
        if (element.src && element.dataset.src && element.src === element.dataset.src) {
            return Promise.resolve();
        }
        
        const lazyType = element.dataset.lazyType;
        
        switch (lazyType) {
            case 'iframe':
                return this.loadIframe(element);
            case 'video':
                return this.loadVideo(element);
            default:
                // Default to image loading for img tags or unknown types
                return this.loadImage(element);
        }
    }

    /**
     * Load image pairs simultaneously
     */
    async loadImagesPairs(slider) {
        const promises = [];
        
        if (slider.beforeImage) {
            promises.push(this.loadMedia(slider.beforeImage));
        }
        
        if (slider.middleImage) {
            promises.push(this.loadMedia(slider.middleImage));
        }
        
        if (slider.afterImage) {
            promises.push(this.loadMedia(slider.afterImage));
        }
        
        await Promise.all(promises);
    }

    /**
     * Load images progressively (before first, middle and after on interaction)
     */
    async loadImagesProgressive(slider) {
        // Load before image first
        if (slider.beforeImage) {
            await this.loadMedia(slider.beforeImage);
        }
        
        // Set up progressive loading for remaining images
        this.setupProgressiveRemainingImages(slider);
    }

    /**
     * Setup progressive loading for middle and after images
     */
    setupProgressiveRemainingImages(slider) {
        const remainingImages = [];
        
        if (slider.middleImage) {
            remainingImages.push(slider.middleImage);
        }
        if (slider.afterImage) {
            remainingImages.push(slider.afterImage);
        }
        
        if (remainingImages.length === 0) return;
        
        const loadRemainingImages = () => {
            remainingImages.forEach(img => this.loadMedia(img));
            // Remove event listeners after first interaction
            slider.container.removeEventListener('mouseenter', loadRemainingImages);
            slider.container.removeEventListener('touchstart', loadRemainingImages);
            slider.container.removeEventListener('click', loadRemainingImages);
        };

        // Load remaining images on any interaction
        slider.container.addEventListener('mouseenter', loadRemainingImages, { once: true });
        slider.container.addEventListener('touchstart', loadRemainingImages, { once: true });
        slider.container.addEventListener('click', loadRemainingImages, { once: true });
        
        // Also load after a delay as fallback
        setTimeout(loadRemainingImages, 2000);
    }

    /**
     * Load individual image
     */
    loadImage(imgElement) {
        return new Promise((resolve, reject) => {
            const imageUrl = imgElement.dataset.src;

            if (!imageUrl) {
                resolve();
                return;
            }

            // If the image is already loaded or being loaded, don't do it again
            if (this.loadedImageUrls.has(imageUrl)) {
                // If the src isn't set yet, set it from the cache
                if (imgElement.src !== imageUrl) {
                    imgElement.src = imageUrl;
                }
                resolve();
                return;
            }

            // Add to the set of loaded URLs to prevent duplicate requests
            this.loadedImageUrls.add(imageUrl);

            // Fade transition
            const fadeDuration = parseInt(this.settings.fadeDuration) || 300;
            
            imgElement.style.transition = `opacity ${fadeDuration}ms ease-in-out`;
            imgElement.style.opacity = '0';
            
            setTimeout(() => {
                // Set the main src directly (no intermediate Image object needed)
                imgElement.src = imageUrl;
                
                // Restore srcset and sizes if they exist
                if (imgElement.dataset.srcset) {
                    imgElement.srcset = imgElement.dataset.srcset;
                    imgElement.removeAttribute('data-srcset');
                }
                if (imgElement.dataset.sizes) {
                    imgElement.sizes = imgElement.dataset.sizes;
                    imgElement.removeAttribute('data-sizes');
                }
                
                // Handle load/error events directly on the img element
                const handleLoad = () => {
                    imgElement.style.opacity = '1';
                    imgElement.classList.remove('coca-bais-lazy-loading');
                    imgElement.classList.add('coca-bais-lazy-loaded');
                    imgElement.removeEventListener('load', handleLoad);
                    imgElement.removeEventListener('error', handleError);
                    resolve();
                };
                
                const handleError = (error) => {
                    imgElement.classList.add('coca-bais-lazy-error');
                    imgElement.removeEventListener('load', handleLoad);
                    imgElement.removeEventListener('error', handleError);
                    reject(error);
                };
                
                imgElement.addEventListener('load', handleLoad, { once: true });
                imgElement.addEventListener('error', handleError, { once: true });
            }, 50);
        });
    }

    /**
     * Load individual iframe
     */
    loadIframe(iframeElement) {
        return new Promise((resolve) => {
            if (!iframeElement.dataset.src) {
                resolve();
                return;
            }

            // Fade transition
            const fadeDuration = parseInt(this.settings.fadeDuration) || 300;
            
            iframeElement.style.transition = `opacity ${fadeDuration}ms ease-in-out`;
            iframeElement.style.opacity = '0';
            
            setTimeout(() => {
                // Load the real iframe src
                iframeElement.src = iframeElement.dataset.src;
                
                // Remove data-src
                iframeElement.removeAttribute('data-src');
                
                // Update classes
                iframeElement.classList.remove('coca-bais-lazy-loading');
                iframeElement.classList.add('coca-bais-lazy-loaded');
                
                // Remove click handler if it exists
                iframeElement.onclick = null;
                
                iframeElement.style.opacity = '1';
                
                // Dispatch custom event for iframe reinitialization
                const customEvent = new CustomEvent('baislIframeLoaded', {
                    detail: {
                        iframe: iframeElement,
                        container: iframeElement.closest('.coca-image-compare-before, .coca-image-compare-after, .coca-image-compare-container')
                    }
                });
                document.dispatchEvent(customEvent);
                
                resolve();
            }, 50);
        });
    }

    /**
     * Load individual video
     */
    loadVideo(videoElement) {
        return new Promise((resolve) => {
            const sources = videoElement.querySelectorAll('source.coca-bais-lazy-source');
            
            if (sources.length === 0) {
                resolve();
                return;
            }

            // Restore source src attributes (handle corrupted data attributes)
            sources.forEach(source => {
                let dataSrc = null;
                
                // Check for corrupted data attributes and fix them
                if (source.dataset.dataSrc) {
                    dataSrc = source.dataset.dataSrc;
                    source.removeAttribute('data-data-src');
                } else if (source.dataset.dataDataSrc) {
                    dataSrc = source.dataset.dataDataSrc;
                    source.removeAttribute('data-data-data-src');
                } else if (source.dataset.src) {
                    dataSrc = source.dataset.src;
                }
                
                if (dataSrc) {
                    source.src = dataSrc;
                    source.removeAttribute('data-src');
                    source.classList.remove('coca-bais-lazy-source');
                }
            });
            
            // Update video classes (remove skeleton placeholder class)
            videoElement.classList.remove('coca-bais-lazy-loading');
            videoElement.classList.add('coca-bais-lazy-loaded');
            
            // Load the video
            videoElement.load();
            
            resolve();
        });
    }

    /**
     * Show loading state
     */
    showLoadingState(slider) {
        // Calculate and preserve container height before images are hidden
        this.preserveContainerHeight(slider);
        
        slider.container.classList.add('coca-bais-loading');
        
        // Add shimmer effect to images
        Array.from(slider.images).forEach(img => {
            img.classList.add('coca-bais-shimmer');
        });
    }
    
    /**
     * Preserve container height during loading
     */
    preserveContainerHeight(slider) {
        const container = slider.container;
        const images = Array.from(slider.images);
        const firstImage = images[0];

        if (!firstImage) {
            container.style.minHeight = '300px';
            container.style.aspectRatio = '16/9';
            return;
        }

        // Use HTML width/height attributes to compute correct dimensions.
        // Relying on offsetHeight is unreliable because the placeholder src
        // (e.g. the 800×800 skeleton SVG) has a different aspect-ratio than
        // the real image, so the browser resolves offsetHeight via the
        // placeholder's 1:1 ratio instead of the image's true ratio.
        const attrWidth  = parseInt(firstImage.getAttribute('width')  || '0', 10);
        const attrHeight = parseInt(firstImage.getAttribute('height') || '0', 10);

        if (attrWidth > 0 && attrHeight > 0) {
            const containerWidth = container.offsetWidth || attrWidth;
            const scale  = containerWidth < attrWidth ? containerWidth / attrWidth : 1;
            const height = Math.round(attrHeight * scale);
            const width  = Math.round(attrWidth  * scale);

            container.style.height    = height + 'px';
            container.style.minHeight = height + 'px';
            container.style.aspectRatio = (width / height).toString();
            return;
        }

        // Fallback: natural dimensions of an already-fully-loaded image (no data-src).
        if (firstImage.naturalWidth > 0 && firstImage.naturalHeight > 0 && !firstImage.dataset.src) {
            const containerWidth = container.offsetWidth || firstImage.naturalWidth;
            const scale  = containerWidth < firstImage.naturalWidth ? containerWidth / firstImage.naturalWidth : 1;
            const height = Math.round(firstImage.naturalHeight * scale);
            const width  = Math.round(firstImage.naturalWidth  * scale);

            container.style.height    = height + 'px';
            container.style.minHeight = height + 'px';
            container.style.aspectRatio = (width / height).toString();
            return;
        }

        // Last resort default
        container.style.minHeight = '300px';
        container.style.aspectRatio = '16/9';
    }

    /**
     * Hide loading state
     */
    hideLoadingState(slider) {
        slider.container.classList.remove('coca-bais-loading');
        
        // Remove shimmer effect
        Array.from(slider.images).forEach(img => {
            img.classList.remove('coca-bais-shimmer');
        });
        
        // Keep height preservation - removing it causes sliders to disappear
        // The height styles are now preserved to maintain slider stability
    }
    
    /**
     * Safely remove height preservation styles (DEPRECATED - causes slider disappearing)
     * This method is kept for reference but should not be called automatically
     */
    removeHeightPreservation(slider) {
        // Method disabled to prevent slider disappearing issues
        // The height preservation styles are now kept permanently for stability
        return;
        
        /* Original implementation - disabled
        const container = slider.container;
        
        // Check if the container has natural height from initialized slider
        const hasNaturalHeight = container.offsetHeight > 0;
        const hasActiveSlider = container.classList.contains('coca-image-compare-container') || 
                               container.querySelector('.coca-image-compare-before');
        
        console.log('BAIS: Checking height removal conditions:', {
            hasNaturalHeight,
            hasActiveSlider,
            currentHeight: container.offsetHeight
        });
        
        // Only remove height styles if slider is properly initialized
        if (hasNaturalHeight && hasActiveSlider) {
            console.log('BAIS: Removing height preservation - slider is initialized');
            container.style.height = '';
            container.style.minHeight = '';
            container.style.aspectRatio = '';
        } else {
            console.log('BAIS: Keeping height preservation - slider not fully initialized yet');
        }
        */
    }

    /**
     * Initialize slider after images are loaded
     */
    initializeSlider(slider) {
        // Re-trigger slider initialization if needed
        const uniqueId = slider.container.getAttribute('data-bais-lazy-id');
        const initEvent = new CustomEvent('bais:reinitialize', {
            detail: { sliderId: uniqueId, originalId: slider.container.id }
        });
        
        slider.container.dispatchEvent(initEvent);
        
        // Additional initialization attempts for both shortcode and Elementor
        setTimeout(() => {
            this.forceSliderInitialization(slider);
        }, 100);
    }
    
    /**
     * Force slider initialization using various methods
     */
    forceSliderInitialization(slider) {
        const container = slider.container;
        
        // Method 1: Try to find and call jQuery image compare if available
        if (typeof jQuery !== 'undefined' && jQuery.fn.imagesCompare) {
            try {
                jQuery(container).imagesCompare();
            } catch (e) {
                // Silent error handling
            }
        }
        
        // Method 2: Try to initialize with original data-settings
        const settings = container.getAttribute('data-settings');
        if (settings) {
            try {
                const parsedSettings = JSON.parse(settings);
                if (typeof jQuery !== 'undefined' && jQuery.fn.imagesCompare) {
                    jQuery(container).imagesCompare(parsedSettings);
                    console.log('BAIS: Initialized with original settings');
                }
            } catch (e) {
                // Silent error handling
            }
        }
        
        // Method 3: Force specific slider recalculation without affecting others
        setTimeout(() => {
            // Only trigger resize on this specific container instead of globally
            if (typeof jQuery !== 'undefined' && jQuery.fn.imagesCompare) {
                try {
                    // Force a specific slider recalculation
                    jQuery(container).trigger('forceCalculate');
                    console.log('BAIS: Triggered specific slider recalculation');
                } catch (e) {
                    // If specific trigger fails, just log it but don't trigger global resize
                    console.log('BAIS: Specific recalculation failed:', e);
                }
            }
        }, 200);
    }

    /**
     * Trigger loaded event
     */
    triggerLoadedEvent(slider) {
        const uniqueId = slider.container.getAttribute('data-bais-lazy-id');
        const loadedEvent = new CustomEvent('bais:lazyloaded', {
            detail: { 
                sliderId: uniqueId,
                originalId: slider.container.id,
                slider: slider 
            }
        });
        
        slider.container.dispatchEvent(loadedEvent);
    }

    /**
     * Handle load error
     */
    handleLoadError(slider, error) {
        slider.container.classList.add('coca-bais-load-error');
        const uniqueId = slider.container.getAttribute('data-bais-lazy-id');
        console.error('Failed to load slider:', uniqueId, 'originalId:', slider.container.id, error);
        
        // Fallback: show original images
        Array.from(slider.images).forEach(img => {
            if (img.dataset.src) {
                img.src = img.dataset.src;
            }
        });
    }

    /**
     * Public method to manually load slider
     */
    loadSliderById(sliderId) {
        this.loadSlider(sliderId);
    }

    /**
     * Mark images as loaded (for when lazy loading is bypassed)
     */
    markImagesAsLoaded(images) {
        Array.from(images).forEach(img => {
            img.classList.remove('coca-bais-lazy-loading');
            img.classList.add('coca-bais-lazy-loaded');
        });
    }

    /**
     * Public method to preload next sliders
     */
    preloadNext(count = 1) {
        const unloadedSliders = Array.from(this.sliders.entries())
            .filter(([sliderId, slider]) => !slider.isLoaded)
            .slice(0, count);
            
        unloadedSliders.forEach(([sliderId]) => {
            this.loadSlider(sliderId);
        });
    }

    /**
     * Debug helper - log current slider states
     */
    debugSliders() {
        console.group('🚀 BAIS Lazy Load Debug Info');
        console.log('Total sliders found:', this.sliders.size);
        console.log('Loaded sliders:', this.loadedSliders.size);
        
        this.sliders.forEach((slider, sliderId) => {
            console.group(`📊 Slider: ${sliderId}`);
            console.log('Original ID:', slider.container.id);
            console.log('Is loaded:', slider.isLoaded);
            console.log('Is visible:', slider.isVisible);
            console.log('Images count:', slider.images.length);
            
            Array.from(slider.images).forEach((img, index) => {
                console.log(`Image ${index + 1}:`, {
                    type: img.dataset.lazyType,
                    hasSrc: !!img.src,
                    hasDataSrc: !!img.dataset.src,
                    classes: Array.from(img.classList).filter(cls => cls.includes('lazy')),
                    isLoading: img.classList.contains('coca-bais-lazy-loading'),
                    isLoaded: img.classList.contains('coca-bais-lazy-loaded')
                });
            });
            console.groupEnd();
        });
        
        console.groupEnd();
        
        return {
            total: this.sliders.size,
            loaded: this.loadedSliders.size,
            sliders: Array.from(this.sliders.entries())
        };
    }
    
    /**
     * Refresh sliders - useful for dynamic content loading
     * This method safely discovers new sliders without affecting loaded ones
     */
    refreshSliders() {
        const previousCount = this.sliders.size;
        
        // Temporarily store existing slider container elements to avoid re-processing
        const existingContainers = new Set();
        this.sliders.forEach(slider => {
            existingContainers.add(slider.container);
        });
        
        // Find new sliders
        const sliderContainers = document.querySelectorAll('.coca-bais-container, .coca-bais-triple-container');
        let newSlidersFound = 0;
        
        sliderContainers.forEach((container, index) => {
            // Skip if this container is already being managed
            if (existingContainers.has(container)) {
                return;
            }
            
            const lazyImages = container.querySelectorAll('.coca-bais-lazy');
            
            if (lazyImages.length > 0) {
                // Create unique slider ID
                const originalId = container.id;
                const uniqueId = originalId ? `${originalId}-lazy-${this.sliders.size + index}` : `bais-slider-${this.sliders.size + index}`;
                
                container.setAttribute('data-bais-lazy-id', uniqueId);
                
                const beforeImage = container.querySelector('.coca-bais-lazy-before');
                const middleImage = container.querySelector('.coca-bais-lazy-middle');
                const afterImage = container.querySelector('.coca-bais-lazy-after');
                
                const needsLoading = Array.from(lazyImages).some(img => img.dataset.src) ||
                                   Array.from(container.querySelectorAll('.coca-bais-lazy-source')).some(source => source.dataset.src);
                
                this.sliders.set(uniqueId, {
                    container: container,
                    images: lazyImages,
                    beforeImage: beforeImage,
                    middleImage: middleImage,
                    afterImage: afterImage,
                    isVisible: false,
                    isLoaded: !needsLoading,
                    priority: this.sliders.size
                });
                
                // Start observing the new slider
                if (this.observer) {
                    this.observer.observe(container);
                }
                
                newSlidersFound++;
                
                if (!needsLoading) {
                    this.markImagesAsLoaded(lazyImages);
                }
            }
        });
        
        return newSlidersFound;
    }
}

// Auto-initialize when DOM is ready (but prevent multiple instances)
if (!window.BAISLazyLoader) {
    document.addEventListener('DOMContentLoaded', () => {
        if (!window.BAISLazyLoader) {
            window.BAISLazyLoader = new BAISLazyLoader();
        }
    });

    // Also initialize immediately if DOM is already loaded
    if (document.readyState !== 'loading') {
        window.BAISLazyLoader = new BAISLazyLoader();
    }
}